import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { MainLayout } from "./layouts/MainLayout";
import { AuthLayout } from "./layouts/AuthLayout";
import { ScrollRestoration } from "./components/ScrollRestoration";
import { Home } from "./routes/home";
import { Pricing } from "./routes/pricing";
import { DiscoverTemplates } from "./routes/discover/templates";
import { DiscoverComponents } from "./routes/discover/components";
import { Login } from "./routes/auth/login";
import { Signup } from "./routes/auth/signup";
import { ForgotPassword } from "./routes/auth/forgot-password";
import { ResetPassword } from "./routes/auth/reset-password";
import { RequireSession } from "./components/RequireSession";
import { NotFound } from "./routes/not-found";
import { ErrorBoundary } from "./routes/error";
import { BestReactEmailTemplates } from "./routes/blog/best-react-email-templates";
import { BuildEmailTemplatesWithAI } from "./routes/blog/build-email-templates-with-ai";
import { ReactEmailVsMjml } from "./routes/blog/react-email-vs-mjml";
import { BlogIndex } from "./routes/blog/index";
import { MarkdownBlogPostRoute } from "./routes/blog/markdown-post";
import { PrivacyPolicy } from "./routes/legal/privacy";
import { TermsOfService } from "./routes/legal/terms";
import { About } from "./routes/about";

const TemplateDetail = lazy(() =>
  import("./routes/templates/[brand]/[slug]").then((m) => ({ default: m.TemplateDetail }))
);
const Dashboard = lazy(() =>
  import("./routes/dashboard").then((m) => ({ default: m.Dashboard }))
);
const Builder = lazy(() =>
  import("./routes/builder").then((m) => ({ default: m.Builder }))
);

function PageLoader() {
  return (
    <section className="flex min-h-[60vh] items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-sky-600" />
        <p className="mt-4 text-sm font-medium text-slate-500">Loading…</p>
      </div>
    </section>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollRestoration />
        <Routes>
          {/* Auth pages — minimal layout */}
          <Route element={<AuthLayout />}>
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/signup" element={<Signup />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
          </Route>

          {/* Main pages — navbar + footer */}
          <Route element={<MainLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/discover/templates" element={<DiscoverTemplates />} />
            <Route path="/discover/components" element={<DiscoverComponents />} />
            <Route path="/templates/:brand/:slug" element={<Suspense fallback={<PageLoader />}><TemplateDetail /></Suspense>} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/best-react-email-templates" element={<BestReactEmailTemplates />} />
            <Route path="/blog/build-email-templates-with-ai" element={<BuildEmailTemplatesWithAI />} />
            <Route path="/blog/react-email-vs-mjml" element={<ReactEmailVsMjml />} />
            <Route path="/blog/:slug" element={<MarkdownBlogPostRoute />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms" element={<TermsOfService />} />
            <Route path="/about" element={<About />} />
            <Route path="/dashboard" element={<RequireSession><Suspense fallback={<PageLoader />}><Dashboard /></Suspense></RequireSession>} />
            <Route
              path="/builder"
              element={<Suspense fallback={<PageLoader />}><Builder /></Suspense>}
            />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
